import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useForm } from "@tanstack/react-form";
import { useTranslation } from "react-i18next";
import { ArrowLeft, CheckCircle2, Circle, Eye, EyeOff, Lock, Plus, TriangleAlert } from "lucide-react";
import * as z from "zod";

import { Badge } from "@/core/components/ui/badge";
import { FLASHPAY_ROLES, RequireRole } from "@/core/auth";
import { apiErrorHandler } from "@/core/api/api-error-handler";
import { DataTableClar } from "@/core/components/shared/data-table";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import { Card, CardContent, CardHeader, CardTitle } from "@/core/components/ui/card";
import { EntitySearchableSelect } from "@/core/components/ui/entity-searchable-select";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import { NoTranslate } from "@/core/components/ui/no-translate";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/core/components/ui/select";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { notifSilence, notifSilenceError, notifSilenceWarning } from "@/core/utils/func/notifApp";
import {
  isKeycloakUserConflict,
  useAddKeycloakUser,
  useCreateKycCase,
  useKycCase,
  useKycCases,
  useParties,
  useParty,
  useSubmitKycCase,
} from "@/features/kyc/api";
import type { KycCaseDto, PartyDto } from "@/features/kyc/api";
import {
  KycCaseDocuments,
  KycCaseReviewDialog,
  PartyFormDialog,
  onboardingDraftStep,
  onboardingDraftsColumns,
} from "@/features/kyc/components";
import type { OnboardingDraftRow } from "@/features/kyc/components";
import {
  createKeycloakUserFieldsSchema,
  createKeycloakUserSchema,
  type KeycloakUserFormValues,
} from "@/features/kyc/schemas/keycloak-user.schema";
import { useKycTranslation } from "@/features/kyc/i18n/use-kyc-translation";
import { useSubmitApprovalRequest } from "@/features/shared/api/use-submit-approval-request";
import { useApproveWalletKyc, useCreateWallet, useWallet, useWallets } from "@/features/wallet/api";
import type { WalletDto } from "@/features/wallet/api";
import { WALLET_KYC_LEVELS } from "@/features/shared/api/enums";
import type { WalletKycLevel } from "@/features/shared/api/enums";
import { useAgentSelectOptions } from "@/features/shared/api";

/** Clés i18n des 7 étapes, dans l'ordre du parcours (guide v1.3). */
const STEP_TITLE_KEYS = [
  "kyc.onboarding.steps.0.title",
  "kyc.onboarding.steps.1.title",
  "kyc.onboarding.steps.2.title",
  "kyc.onboarding.steps.3.title",
  "kyc.onboarding.steps.4.title",
  "kyc.onboarding.steps.5.title",
  "kyc.onboarding.steps.6.title",
] as const;

const STEP_STATE_KEYS = {
  done: "kyc.onboarding.state.done",
  current: "kyc.onboarding.state.current",
  locked: "kyc.onboarding.state.locked",
} as const;

const STORAGE_KEY = "fp.onboarding.v1";

interface EmailVerificationInfo {
  required: boolean;
  sent: boolean;
}

/** Brouillon de saisie de l'étape 1 — JAMAIS les mots de passe (ni ici, ni
 *  dans le state parent, ni dans le localStorage). */
interface Step1Draft {
  username: string;
  email: string;
  firstName: string;
  lastName: string;
}

interface OnboardingSnapshot {
  keycloakUserId: string | null;
  emailVerification: EmailVerificationInfo | null;
  partyId: string | null;
  walletId: string | null;
  kycCaseId: string | null;
  screeningRequested: boolean;
  step1Draft: Step1Draft | null;
}

const EMPTY_SNAPSHOT: OnboardingSnapshot = {
  keycloakUserId: null,
  emailVerification: null,
  partyId: null,
  walletId: null,
  kycCaseId: null,
  screeningRequested: false,
  step1Draft: null,
};

const readSnapshot = (): OnboardingSnapshot => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY_SNAPSHOT;
    return { ...EMPTY_SNAPSHOT, ...JSON.parse(raw) };
  } catch {
    return EMPTY_SNAPSHOT;
  }
};

const writeSnapshot = (snapshot: OnboardingSnapshot) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    /* localStorage indisponible */
  }
};

const clearSnapshot = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* localStorage indisponible */
  }
};

/** Brouillon local affichable en vue liste : saisie étape 1 non vide, ou
 *  compte Keycloak créé sans Party enregistrée (invisible côté serveur). */
const hasLocalDraft = (snapshot: OnboardingSnapshot): boolean =>
  Boolean(
    (snapshot.keycloakUserId && !snapshot.partyId) ||
      (snapshot.step1Draft &&
        Object.values(snapshot.step1Draft).some((value) => value.trim() !== ""))
  );

/**
 * Parcours d'inscription client en 7 étapes (guide v1.3), piloté depuis le
 * back-office pour le compte du client :
 *   1. Compte Keycloak (`Users/add`, endpoint public) → 2. Party
 *   (keycloakUserId obligatoire) → 3. Portefeuille (PENDING_KYC/TIER0)
 *   → 4. Dossier KYC + pièces → 5. Criblage sanctions CLEAR → 6. Revue
 *   → 7. kyc-approval (orchestration explicite — Kafka non câblé, le bouton
 *   remplace l'événement).
 *
 * Deux vues pilotées par l'URL : la **liste « Parcours en cours »** (défaut,
 * sans `?partyId=`) et le **wizard** 7 étapes (`?partyId=` présent, ou état
 * local `started` pour un parcours sans identifiants encore créés). Le wizard
 * est repris depuis l'URL (`?partyId=&walletId=&kycCaseId=`) et un snapshot
 * `localStorage` (survit à la fermeture du navigateur) ; le snapshot sert
 * aussi à détecter le brouillon local étape 1 (keycloakUserId sans partyId),
 * invisible côté serveur.
 */
export function OnboardingPage() {
  const { t } = useTypedTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlPartyId = searchParams.get("partyId");

  // Vue wizard sans identifiants dans l'URL : parcours tout juste démarré
  // (« Nouveau parcours » ou reprise du brouillon local étape 1).
  const [started, setStarted] = useState(false);
  // Brouillon local : saisie étape 1 non soumise, ou compte Keycloak créé
  // (étape 1) mais Party pas encore enregistrée — invisible côté serveur,
  // détecté via le snapshot seul.
  const [localDraft, setLocalDraft] = useState<OnboardingSnapshot | null>(() => {
    const stored = readSnapshot();
    return hasLocalDraft(stored) ? stored : null;
  });

  // Reprise au montage : l'URL seule alimente les identifiants ; le snapshot
  // ne complète que le parcours correspondant (survie à un refresh).
  const initial = useMemo(() => {
    const stored = readSnapshot();
    const matching =
      urlPartyId && stored.partyId === urlPartyId ? stored : null;
    return {
      keycloakUserId: matching?.keycloakUserId ?? null,
      emailVerification: matching?.emailVerification ?? null,
      partyId: urlPartyId,
      walletId: searchParams.get("walletId") || matching?.walletId || null,
      kycCaseId: searchParams.get("kycCaseId") || matching?.kycCaseId || null,
      screeningRequested: matching?.screeningRequested ?? false,
      step1Draft: matching?.step1Draft ?? null,
    };
    // Lecture initiale uniquement — la sync URL se fait dans les effets ci-dessous.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [keycloakUserId, setKeycloakUserId] = useState<string | null>(
    initial.keycloakUserId
  );
  const [emailVerification, setEmailVerification] =
    useState<EmailVerificationInfo | null>(initial.emailVerification);
  const [partyId, setPartyId] = useState<string | null>(initial.partyId);
  const [walletId, setWalletId] = useState<string | null>(initial.walletId);
  const [kycCaseId, setKycCaseId] = useState<string | null>(initial.kycCaseId);
  const [screeningRequested, setScreeningRequested] = useState(
    initial.screeningRequested
  );
  const [step1Draft, setStep1Draft] = useState<Step1Draft | null>(
    initial.step1Draft
  );

  const { data: party } = useParty(partyId ?? "", Boolean(partyId));
  const { data: wallet } = useWallet(walletId ?? "", Boolean(walletId));
  const { data: kycCase } = useKycCase(kycCaseId ?? "", Boolean(kycCaseId));

  // Reprise d'un parcours via l'URL après le montage (action « Reprendre » de
  // la liste : navigation vers la même route, sans remontage du composant).
  // leavingRef : au retour à la liste, React traite le reset d'état et la
  // navigation en deux commits — dans le commit intermédiaire (état déjà nul,
  // `urlPartyId` encore présent), sans ce garde-fou l'effet ré-hydraterait le
  // parcours depuis l'URL et annulerait la sortie.
  const leavingRef = useRef(false);
  useEffect(() => {
    if (!urlPartyId) {
      leavingRef.current = false;
      return;
    }
    if (leavingRef.current || urlPartyId === partyId) return;
    const stored = readSnapshot();
    const matching = stored.partyId === urlPartyId ? stored : null;
    setKeycloakUserId(matching?.keycloakUserId ?? null);
    setEmailVerification(matching?.emailVerification ?? null);
    setPartyId(urlPartyId);
    setWalletId(searchParams.get("walletId") || matching?.walletId || null);
    setKycCaseId(searchParams.get("kycCaseId") || matching?.kycCaseId || null);
    setScreeningRequested(matching?.screeningRequested ?? false);
    setStep1Draft(matching?.step1Draft ?? null);
    setStarted(false);
  }, [urlPartyId, partyId, searchParams]);

  const inWizard = started || Boolean(urlPartyId);

  // Persistance snapshot + sync URL : vue wizard uniquement — en vue liste,
  // cet effet écraserait le snapshot avec des nulls.
  useEffect(() => {
    if (!inWizard) return;
    writeSnapshot({
      keycloakUserId,
      emailVerification,
      partyId,
      walletId,
      kycCaseId,
      screeningRequested,
      step1Draft,
    });
    const next = new URLSearchParams();
    if (partyId) next.set("partyId", partyId);
    if (walletId) next.set("walletId", walletId);
    if (kycCaseId) next.set("kycCaseId", kycCaseId);
    setSearchParams(next, { replace: true });
  }, [
    inWizard,
    keycloakUserId,
    emailVerification,
    partyId,
    walletId,
    kycCaseId,
    screeningRequested,
    step1Draft,
    setSearchParams,
  ]);

  const resetJourney = () => {
    setKeycloakUserId(null);
    setEmailVerification(null);
    setPartyId(null);
    setWalletId(null);
    setKycCaseId(null);
    setScreeningRequested(false);
    setStep1Draft(null);
  };

  const handleNewJourney = () => {
    // Un seul brouillon local possible : démarrer un nouveau parcours écrase
    // le brouillon étape 1 existant — confirmation préalable.
    if (
      hasLocalDraft(readSnapshot()) &&
      !window.confirm(t("kyc.onboarding.drafts.localDraft.confirmDiscard"))
    )
      return;
    clearSnapshot();
    resetJourney();
    setLocalDraft(null);
    setStarted(true);
  };

  const handleResumeLocalDraft = () => {
    const stored = readSnapshot();
    resetJourney();
    setKeycloakUserId(stored.keycloakUserId);
    setEmailVerification(stored.emailVerification);
    setScreeningRequested(stored.screeningRequested);
    setStep1Draft(stored.step1Draft);
    setStarted(true);
  };

  // Abandon du brouillon local : effacement local uniquement (pas de mutation
  // HTTP → pas de preview de confirmation, simple confirm natif).
  const handleDiscardLocalDraft = () => {
    if (!window.confirm(t("kyc.onboarding.drafts.localDraft.confirmDiscard")))
      return;
    clearSnapshot();
    setLocalDraft(null);
    setStep1Draft(null);
  };

  const handleBackToList = () => {
    leavingRef.current = true;
    if (partyId) {
      // Parcours ancré côté serveur (Party créée) : il reste visible dans le
      // tableau des brouillons, le snapshot local n'a plus d'utilité.
      clearSnapshot();
      setLocalDraft(null);
    } else {
      // Parcours encore local (étape 1) : le snapshot est l'UNIQUE point
      // d'ancrage — il est préservé pour alimenter la carte « Brouillon
      // local » de la liste (sinon la saisie est perdue au retour).
      const draft: OnboardingSnapshot = {
        ...EMPTY_SNAPSHOT,
        keycloakUserId,
        emailVerification,
        step1Draft,
      };
      writeSnapshot(draft);
      setLocalDraft(hasLocalDraft(draft) ? draft : null);
    }
    resetJourney();
    setStarted(false);
    setSearchParams({});
  };

  // Compte créé à l'étape 1, saisi manuellement (409) ou relu depuis la Party
  // (reprise d'un parcours via l'URL).
  const effectiveKeycloakUserId =
    keycloakUserId ?? party?.keycloakUserId ?? null;

  const screeningClear = party?.sanctionsScreeningStatus === "CLEAR";
  const walletActive = wallet?.status === "ACTIVE";

  const stepStates = [
    Boolean(effectiveKeycloakUserId) || Boolean(partyId),
    Boolean(party),
    Boolean(wallet),
    Boolean(
      kycCase && ["SUBMITTED", "IN_REVIEW", "APPROVED"].includes(kycCase.status)
    ),
    screeningClear,
    kycCase?.status === "APPROVED",
    walletActive,
  ];
  const currentStep = stepStates.findIndex((done) => !done);

  const stepTitle = (index: number) => t(STEP_TITLE_KEYS[index]);

  if (!inWizard) {
    return (
      <section className="flex flex-col gap-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">
              {t("kyc.onboarding.title")}
            </h1>
            <p className="text-muted-foreground">
              {t("kyc.onboarding.subtitle")}
            </p>
          </div>
          <Button onClick={handleNewJourney}>
            <Plus className="mr-2 h-4 w-4" />
            {t("kyc.onboarding.drafts.new")}
          </Button>
        </div>

        {localDraft ? (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t("kyc.onboarding.drafts.localDraft.title")}
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <p className="text-sm text-muted-foreground">
                {t("kyc.onboarding.drafts.localDraft.description")}
              </p>
              <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning-muted p-3">
                <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
                <p className="text-sm text-warning">
                  {t("kyc.onboarding.keycloakUser.continueWarning")}
                </p>
              </div>
              {localDraft.step1Draft ? (
                <p className="text-sm">
                  <NoTranslate as="span" className="font-medium">
                    {[
                      [localDraft.step1Draft.firstName, localDraft.step1Draft.lastName]
                        .filter(Boolean)
                        .join(" "),
                      localDraft.step1Draft.email,
                      localDraft.step1Draft.username,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </NoTranslate>
                </p>
              ) : null}
              {localDraft.keycloakUserId ? (
                <p className="text-sm">
                  <NoTranslate as="span" className="font-medium">
                    {localDraft.keycloakUserId}
                  </NoTranslate>
                </p>
              ) : null}
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={handleResumeLocalDraft}>
                  {t("kyc.onboarding.drafts.localDraft.resume")}
                </Button>
                <Button variant="destructive" onClick={handleDiscardLocalDraft}>
                  {t("kyc.onboarding.drafts.localDraft.discard")}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : null}

        <OnboardingDraftsTable onNew={handleNewJourney} />
      </section>
    );
  }

  return (
    <section className="flex flex-col gap-4">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">
          {t("kyc.onboarding.title")}
        </h1>
        <p className="text-muted-foreground">{t("kyc.onboarding.subtitle")}</p>
        {partyId ? (
          <p className="mt-1 text-xs text-muted-foreground">
            {t("kyc.onboarding.resume.hint")}
          </p>
        ) : null}
      </div>

      <Button variant="ghost" className="self-start" onClick={handleBackToList}>
        <ArrowLeft className="mr-2 h-4 w-4" />
        {t("kyc.onboarding.drafts.backToList")}
      </Button>

      <StepCard
        index={0}
        title={stepTitle(0)}
        state={stepStates[0] ? "done" : currentStep === 0 ? "current" : "locked"}
      >
        {effectiveKeycloakUserId ? (
          <div className="flex flex-col gap-1">
            {step1Draft ? (
              <p className="text-sm">
                <NoTranslate as="span" className="font-medium">
                  {[
                    [step1Draft.firstName, step1Draft.lastName]
                      .filter(Boolean)
                      .join(" "),
                    step1Draft.email,
                    step1Draft.username,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </NoTranslate>
              </p>
            ) : null}
            <p className="text-sm">
              <NoTranslate as="span" className="font-medium">
                {effectiveKeycloakUserId}
              </NoTranslate>
            </p>
            {emailVerification?.required ? (
              <p className="text-xs text-muted-foreground">
                {t(
                  emailVerification.sent
                    ? "kyc.onboarding.keycloakUser.emailVerificationSent"
                    : "kyc.onboarding.keycloakUser.emailVerificationRequired"
                )}
              </p>
            ) : null}
            {!partyId ? (
              <div className="mt-1 flex items-start gap-2 rounded-md border border-warning/40 bg-warning-muted p-3">
                <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
                <p className="text-sm text-warning">
                  {t("kyc.onboarding.keycloakUser.continueWarning")}
                </p>
              </div>
            ) : null}
          </div>
        ) : partyId ? null : (
          <StepKeycloakUser
            initialValues={{ ...EMPTY_KEYCLOAK_USER_VALUES, ...(step1Draft ?? {}) }}
            onValuesChange={setStep1Draft}
            onCreated={(id, verification) => {
              setKeycloakUserId(id);
              setEmailVerification(verification);
              // Les infos saisies sont CONSERVÉES comme point d'ancrage
              // lisible (carte brouillon + récap ci-dessus) jusqu'à la
              // création de la Party — ensuite le serveur prend le relais.
            }}
          />
        )}
      </StepCard>

      <StepCard
        index={1}
        title={stepTitle(1)}
        state={stepStates[1] ? "done" : currentStep === 1 ? "current" : "locked"}
      >
        {party ? (
          <p className="text-sm">
            <NoTranslate as="span" className="font-medium">
              {[party.firstName, party.lastName].filter(Boolean).join(" ")}
            </NoTranslate>{" "}
            — <NoTranslate as="span">{party.id}</NoTranslate>
          </p>
        ) : effectiveKeycloakUserId ? (
          <StepParty
            keycloakUserId={effectiveKeycloakUserId}
            onCreated={(created) => {
              setPartyId(created.id);
              // La Party existe côté serveur : elle devient le point
              // d'ancrage du parcours, le brouillon de saisie de l'étape 1
              // est soldé.
              setStep1Draft(null);
            }}
          />
        ) : (
          <LockedHint />
        )}
      </StepCard>

      <StepCard
        index={2}
        title={stepTitle(2)}
        state={stepStates[2] ? "done" : currentStep === 2 ? "current" : "locked"}
      >
        {wallet ? (
          <p className="text-sm">
            <NoTranslate as="span" className="font-medium">
              {wallet.walletNumber}
            </NoTranslate>{" "}
            — <NoTranslate as="span">{wallet.id}</NoTranslate> (
            <NoTranslate as="span">{wallet.status}</NoTranslate>)
          </p>
        ) : party ? (
          <StepWallet
            party={party}
            onCreated={(created) => setWalletId(created.id ?? null)}
          />
        ) : (
          <LockedHint />
        )}
      </StepCard>

      <StepCard
        index={3}
        title={stepTitle(3)}
        state={stepStates[3] ? "done" : currentStep === 3 ? "current" : "locked"}
      >
        {party ? (
          <StepKycCase
            partyId={party.id}
            kycCaseId={kycCaseId}
            kycCase={kycCase}
            onCreated={setKycCaseId}
          />
        ) : (
          <LockedHint />
        )}
      </StepCard>

      <StepCard
        index={4}
        title={stepTitle(4)}
        state={stepStates[4] ? "done" : currentStep === 4 ? "current" : "locked"}
      >
        {party && stepStates[3] ? (
          <StepScreening
            partyId={party.id}
            done={screeningClear}
            requested={screeningRequested}
            onRequested={() => setScreeningRequested(true)}
          />
        ) : (
          <LockedHint />
        )}
      </StepCard>

      <StepCard
        index={5}
        title={stepTitle(5)}
        state={stepStates[5] ? "done" : currentStep === 5 ? "current" : "locked"}
      >
        {kycCase && screeningClear && !stepStates[5] ? (
          <StepReview kycCaseId={kycCase.id} />
        ) : stepStates[5] ? (
          <p className="text-sm text-success">{t("kyc.onboarding.review.approved")}</p>
        ) : (
          <LockedHint />
        )}
      </StepCard>

      <StepCard
        index={6}
        title={stepTitle(6)}
        state={stepStates[6] ? "done" : currentStep === 6 ? "current" : "locked"}
      >
        {wallet && kycCase && stepStates[5] && !walletActive ? (
          <StepActivation
            walletId={wallet.id ?? ""}
            kycLevel={kycCase.levelRequested}
          />
        ) : walletActive ? (
          <p className="text-sm font-medium text-success">
            {t("kyc.onboarding.activation.done")}
          </p>
        ) : (
          <LockedHint />
        )}
      </StepCard>
    </section>
  );
}

/* --------------------------------- sous-composants ------------------------------ */

/**
 * Vue liste « Parcours en cours » : brouillons serveur = Parties au statut KYC
 * NONE ou PENDING sans portefeuille ACTIVE, croisées en mémoire avec les
 * portefeuilles et dossiers KYC (200 premiers de chaque liste, borne §4.5).
 * Navigation uniquement — aucune mutation HTTP, pas de preview requise.
 */
function OnboardingDraftsTable({ onNew }: { onNew: () => void }) {
  const { t } = useTypedTranslation();
  // Pas de filtre serveur `kycStatus` : le backend ne retourne pas les Parties
  // NONE sur un tel filtre (NONE ≈ non renseigné). Comportement identique à
  // la page Parties (`filter: {}`) ; les critères sont appliqués côté client.
  const partiesQuery = useParties({ filter: {}, pageSize: 200 });
  const walletsQuery = useWallets({ pageSize: 200 });
  const casesQuery = useKycCases({ pageSize: 200 });

  const isLoading =
    partiesQuery.isLoading || walletsQuery.isLoading || casesQuery.isLoading;
  const isFetching =
    partiesQuery.isFetching || walletsQuery.isFetching || casesQuery.isFetching;
  const error = partiesQuery.error ?? walletsQuery.error ?? casesQuery.error;
  const refetchAll = () => {
    void partiesQuery.refetch();
    void walletsQuery.refetch();
    void casesQuery.refetch();
  };

  const rows = useMemo<OnboardingDraftRow[]>(() => {
    // Portefeuille le plus récent par Party ; un wallet ACTIVE sort la Party
    // des brouillons (parcours terminé).
    const activeWalletParties = new Set<string>();
    const walletByParty = new Map<string, WalletDto>();
    for (const item of walletsQuery.data?.items ?? []) {
      if (!item.ownerPartyId) continue;
      if (item.status === "ACTIVE") activeWalletParties.add(item.ownerPartyId);
      const current = walletByParty.get(item.ownerPartyId);
      if (!current || (item.createdAt ?? "") > (current.createdAt ?? "")) {
        walletByParty.set(item.ownerPartyId, item);
      }
    }

    // Dossier KYC le plus récent par Party (submittedAt, sinon premier
    // rencontré — ordre arbitraire mais stable).
    const caseByParty = new Map<string, KycCaseDto>();
    for (const item of casesQuery.data?.items ?? []) {
      const current = caseByParty.get(item.partyId);
      if (!current || (item.submittedAt ?? "") > (current.submittedAt ?? "")) {
        caseByParty.set(item.partyId, item);
      }
    }

    // Brouillon = Party sans wallet ACTIVE et non REJECTED (impasse). Les
    // kycStatus TIER1/TIER2 sans wallet ACTIVE sont inclus : étape 7 en attente.
    const drafts: OnboardingDraftRow[] = [];
    for (const draftParty of partiesQuery.data?.items ?? []) {
      if (activeWalletParties.has(draftParty.id)) continue;
      if (draftParty.kycStatus === "REJECTED") continue;
      const draftWallet = walletByParty.get(draftParty.id) ?? null;
      const draftCase = caseByParty.get(draftParty.id) ?? null;
      drafts.push({
        id: draftParty.id,
        party: draftParty,
        wallet: draftWallet,
        kycCase: draftCase,
        step: onboardingDraftStep(draftParty, draftWallet, draftCase),
      });
    }
    return drafts;
  }, [partiesQuery.data, walletsQuery.data, casesQuery.data]);

  // État vide dédié avec CTA (EmptyTable de DataTableClar n'accepte pas de
  // contenu additionnel — l'état vide est donc rendu à la place du tableau).
  if (!isLoading && !error && rows.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
          <p className="font-medium">{t("kyc.onboarding.drafts.empty")}</p>
          <p className="max-w-md text-sm text-muted-foreground">
            {t("kyc.onboarding.drafts.emptyDescription")}
          </p>
          <Button onClick={onNew}>
            <Plus className="mr-2 h-4 w-4" />
            {t("kyc.onboarding.drafts.new")}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    // paginationMode="client" : les lignes sont un agrégat local (fusion
    // mémoire de 3 listes bornées à 200) — la pagination server suppose une
    // seule source paginée par l'API, inapplicable ici.
    <DataTableClar
      data={rows}
      columnsClar={onboardingDraftsColumns}
      title={t("kyc.onboarding.drafts.title")}
      exportFileName="kyc-onboarding-drafts"
      hideTitle={false}
      paginationMode="client"
      loading={{ isLoading, isFetching }}
      error={
        error ? { message: error.message, refetch: refetchAll } : undefined
      }
    />
  );
}

function StepCard({
  index,
  title,
  state,
  children,
}: {
  index: number;
  title: string;
  state: "done" | "current" | "locked";
  children: React.ReactNode;
}) {
  const { t } = useTypedTranslation();
  return (
    <Card className={state === "locked" ? "opacity-60" : undefined}>
      <CardHeader className="flex-row items-center gap-3 space-y-0">
        {state === "done" ? (
          <CheckCircle2 className="size-5 text-success" />
        ) : state === "current" ? (
          <Circle className="size-5 text-primary" />
        ) : (
          <Lock className="size-5 text-muted-foreground" />
        )}
        <CardTitle className="text-base">
          {index + 1}. {title}
        </CardTitle>
        <Badge variant={state === "done" ? "default" : "outline"}>
          {t(STEP_STATE_KEYS[state])}
        </Badge>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function LockedHint() {
  const { t } = useTypedTranslation();
  return (
    <p className="text-sm text-muted-foreground">{t("kyc.onboarding.locked")}</p>
  );
}

function RoleRequiredHint() {
  const { t } = useTypedTranslation();
  return (
    <p className="text-sm text-muted-foreground">
      {t("kyc.onboarding.roleRequired")}
    </p>
  );
}

const EMPTY_KEYCLOAK_USER_VALUES: KeycloakUserFormValues = {
  username: "",
  email: "",
  firstName: "",
  lastName: "",
  password: "",
  confirmPassword: "",
};

/**
 * Étape 1 — création du compte Keycloak (`Users/add`, endpoint PUBLIC :
 * register hors scope de la preview de confirmation). Sur 409 (username /
 * email déjà pris), l'agent peut saisir manuellement l'identifiant d'un
 * compte existant créé hors parcours pour continuer.
 *
 * `initialValues` préremplit le formulaire (brouillon local, mots de passe
 * toujours vides) ; `onValuesChange` remonte la saisie des 4 champs non
 * sensibles au parent pour le snapshot — les mots de passe ne quittent
 * jamais le formulaire.
 */
function StepKeycloakUser({
  initialValues,
  onValuesChange,
  onCreated,
}: {
  initialValues: KeycloakUserFormValues;
  onValuesChange: (draft: Step1Draft) => void;
  onCreated: (
    keycloakUserId: string,
    emailVerification: EmailVerificationInfo | null
  ) => void;
}) {
  const { t } = useTypedTranslation();
  const { i18n } = useTranslation();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : dépendance voulue, le schéma zod traduit est reconstruit à chaque changement de langue (convention login.schema.ts).
  const schema = useMemo(() => createKeycloakUserSchema(), [i18n.language]);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- idem : validation champ par champ via le ZodObject (`.shape`).
  const fieldsSchema = useMemo(() => createKeycloakUserFieldsSchema(), [i18n.language]);

  const addUser = useAddKeycloakUser();
  const [conflict, setConflict] = useState(false);
  const [manualId, setManualId] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const form = useForm({
    defaultValues: initialValues,
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success) return;
      const { confirmPassword: _confirmPassword, ...payload } = parsed.data;
      addUser.mutate(payload, {
        onSuccess: (data) => {
          notifSilence(t("kyc.onboarding.keycloakUser.success"));
          const verification =
            data.emailVerificationRequired !== undefined ||
            data.emailVerificationSent !== undefined
              ? {
                  required: Boolean(data.emailVerificationRequired),
                  sent: Boolean(data.emailVerificationSent),
                }
              : null;
          onCreated(data.keycloakUserId, verification);
        },
        onError: (error) => {
          // 409 : message de conflit dédié + saisie manuelle d'un compte
          // existant. 400 : politique mot de passe — le message backend
          // (getBackendErrorMessage via apiErrorHandler) prime.
          if (isKeycloakUserConflict(error)) {
            setConflict(true);
            return;
          }
          notifSilenceError(apiErrorHandler(error));
        },
      });
    },
  });

  const fieldError =
    (name: keyof KeycloakUserFormValues) => (props: { value: unknown }) => {
      const shape = fieldsSchema.shape[name];
      const result = shape.safeParse(props.value ?? "");
      return result.success ? undefined : result.error.issues[0]?.message;
    };

  const submitManualId = () => {
    const parsed = z.string().uuid().safeParse(manualId.trim());
    if (!parsed.success) {
      notifSilenceError(t("invalidFieldError"));
      return;
    }
    onCreated(parsed.data, null);
  };

  return (
    <div className="flex max-w-2xl flex-col gap-4">
      <p className="text-sm text-muted-foreground">
        {t("kyc.onboarding.keycloakUser.description")}
      </p>
      <form
        className="grid grid-cols-1 gap-4 sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          void form.handleSubmit();
        }}
      >
        {(
          [
            ["username", "text"],
            ["email", "email"],
            ["firstName", "text"],
            ["lastName", "text"],
            ["password", "password"],
          ] as const
        ).map(([name, type]) => (
          <form.Field
            key={name}
            name={name}
            validators={{ onChange: fieldError(name) }}
          >
            {(field) => (
              <div className="space-y-1.5">
                <Label htmlFor={`kc-${name}`}>
                  {t(`kyc.onboarding.keycloakUser.${name}`)}
                </Label>
                {type === "password" ? (
                  <div className="relative">
                    <Input
                      id={`kc-${name}`}
                      type={showPassword ? "text" : "password"}
                      className="pr-10"
                      autoComplete="new-password"
                      placeholder={t(`kyc.onboarding.keycloakUser.${name}Placeholder`)}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-1 top-1/2 size-8 -translate-y-1/2"
                      aria-label={t("auth.login.togglePassword")}
                      onClick={() => setShowPassword((v) => !v)}
                    >
                      {showPassword ? <EyeOff /> : <Eye />}
                    </Button>
                  </div>
                ) : (
                  <Input
                    id={`kc-${name}`}
                    type={type}
                    className="notranslate"
                    placeholder={t(`kyc.onboarding.keycloakUser.${name}Placeholder`)}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => {
                      field.handleChange(e.target.value);
                      // Brouillon étape 1 : remonte les 4 champs non sensibles
                      // (jamais password / confirmPassword).
                      const current = {
                        ...field.form.state.values,
                        [name]: e.target.value,
                      };
                      onValuesChange({
                        username: current.username,
                        email: current.email,
                        firstName: current.firstName,
                        lastName: current.lastName,
                      });
                    }}
                  />
                )}
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">
                    {field.state.meta.errors[0]}
                  </p>
                )}
              </div>
            )}
          </form.Field>
        ))}

        <form.Field
          name="confirmPassword"
          validators={{
            onChange: ({ value, fieldApi }) => {
              if (!value) return t("requiredFieldError");
              return value === fieldApi.form.getFieldValue("password")
                ? undefined
                : t("kyc.onboarding.keycloakUser.passwordMismatch");
            },
          }}
        >
          {(field) => (
            <div className="space-y-1.5">
              <Label htmlFor="kc-confirmPassword">
                {t("kyc.onboarding.keycloakUser.confirmPassword")}
              </Label>
              <div className="relative">
                <Input
                  id="kc-confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  className="pr-10"
                  autoComplete="new-password"
                  placeholder={t(
                    "kyc.onboarding.keycloakUser.confirmPasswordPlaceholder"
                  )}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 size-8 -translate-y-1/2"
                  aria-label={t("auth.login.togglePassword")}
                  onClick={() => setShowConfirmPassword((v) => !v)}
                >
                  {showConfirmPassword ? <EyeOff /> : <Eye />}
                </Button>
              </div>
              {field.state.meta.errors.length > 0 && (
                <p className="text-sm text-destructive">
                  {field.state.meta.errors[0]}
                </p>
              )}
            </div>
          )}
        </form.Field>

        <div className="sm:col-span-2">
          <Button type="submit" disabled={addUser.isPending}>
            {addUser.isPending ? <ButtonSpinner /> : null}
            {t("kyc.onboarding.keycloakUser.submit")}
          </Button>
        </div>
      </form>

      {conflict ? (
        <div className="flex max-w-md flex-col gap-3 rounded-md border border-warning/40 bg-warning-muted p-3">
          <p className="text-sm text-warning">
            {t("kyc.onboarding.keycloakUser.conflict")}
          </p>
          <div className="space-y-1.5">
            <Label htmlFor="manual-kc-id">
              {t("kyc.onboarding.keycloakUser.manualEntry")}
            </Label>
            <Input
              id="manual-kc-id"
              className="notranslate"
              value={manualId}
              onChange={(e) => setManualId(e.target.value)}
              placeholder={t("kyc.onboarding.keycloakUser.manualEntryPlaceholder")}
            />
          </div>
          <Button
            variant="outline"
            className="self-start"
            onClick={submitManualId}
            disabled={!manualId.trim()}
          >
            {t("kyc.onboarding.keycloakUser.manualEntrySubmit")}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function StepParty({
  keycloakUserId,
  onCreated,
}: {
  keycloakUserId: string;
  onCreated: (party: PartyDto) => void;
}) {
  const { t } = useTypedTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button onClick={() => setOpen(true)}>{t("kyc.parties.create")}</Button>
      <PartyFormDialog
        open={open}
        onOpenChange={setOpen}
        onCreated={onCreated}
        lockedKeycloakUserId={keycloakUserId}
      />
    </>
  );
}

function StepWallet({
  party,
  onCreated,
}: {
  party: PartyDto;
  onCreated: (wallet: WalletDto) => void;
}) {
  const { t } = useTypedTranslation();
  const [keycloakUserId, setKeycloakUserId] = useState(
    party.keycloakUserId ?? ""
  );
  const createWallet = useCreateWallet();

  return (
    <div className="flex max-w-md flex-col gap-3">
      <div className="space-y-1.5">
        <Label htmlFor="wallet-kc-user">{t("kyc.onboarding.wallet.keycloakUserId")}</Label>
        <Input
          id="wallet-kc-user"
          className="notranslate"
          value={keycloakUserId}
          onChange={(e) => setKeycloakUserId(e.target.value)}
          placeholder={t("kyc.onboarding.wallet.keycloakUserIdPlaceholder")}
        />
        <p className="text-xs text-muted-foreground">
          {t("kyc.onboarding.wallet.keycloakUserIdHint")}
        </p>
      </div>
      <Button
        className="self-start"
        disabled={createWallet.isPending}
        onClick={() =>
          createWallet.mutate(
            {
              ownerType: party.partyType,
              ownerPartyId: party.id,
              keycloakUserId: keycloakUserId || party.keycloakUserId || undefined,
              currency: "GNF",
              kycLevel: "TIER0",
              status: "PENDING_KYC",
            },
            {
              onSuccess: (data) => {
                notifSilence(t("kyc.onboarding.wallet.created"));
                onCreated(data);
              },
              onError: (error) => notifSilenceError(apiErrorHandler(error)),
            }
          )
        }
      >
        {createWallet.isPending ? <ButtonSpinner /> : null}
        {t("kyc.onboarding.wallet.create")}
      </Button>
    </div>
  );
}

function StepKycCase({
  partyId,
  kycCaseId,
  kycCase,
  onCreated,
}: {
  partyId: string;
  kycCaseId: string | null;
  kycCase: KycCaseDto | undefined;
  onCreated: (id: string) => void;
}) {
  const { t } = useTypedTranslation();
  const [levelRequested, setLevelRequested] = useState<WalletKycLevel>("TIER1");
  const [assignedAgentId, setAssignedAgentId] = useState("");
  const createCase = useCreateKycCase();
  const submitCase = useSubmitKycCase(kycCaseId ?? "");
  const { options: agentOptions, isLoading: agentsLoading } =
    useAgentSelectOptions(true);

  if (!kycCaseId) {
    return (
      <div className="flex max-w-md flex-col gap-3">
        <div className="space-y-1.5">
          <Label>{t("kyc.cases.form.levelRequested")}</Label>
          <Select
            value={levelRequested}
            onValueChange={(v) => setLevelRequested(v as WalletKycLevel)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WALLET_KYC_LEVELS.filter((l) => l !== "TIER0").map((level) => (
                <SelectItem key={level} value={level} className="notranslate">
                  {level}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="assigned-agent">
            {t("kyc.onboarding.cases.assignedAgentId")}
          </Label>
          <EntitySearchableSelect
            id="assigned-agent"
            options={agentOptions}
            value={assignedAgentId}
            onValueChange={setAssignedAgentId}
            placeholder={t("kyc.onboarding.cases.assignedAgentIdPlaceholder")}
            isLoading={agentsLoading}
            clearable
          />
        </div>
        <Button
          className="self-start"
          disabled={createCase.isPending}
          onClick={() =>
            createCase.mutate(
              {
                partyId,
                levelRequested,
                assignedAgentId: assignedAgentId.trim() || null,
              },
              {
                onSuccess: (data) => {
                  notifSilence(t("kyc.cases.feedback.created"));
                  if (data && typeof data === "object" && "id" in data) {
                    onCreated((data as unknown as KycCaseDto).id);
                  }
                },
                onError: (error) => notifSilenceError(apiErrorHandler(error)),
              }
            )
          }
        >
          {createCase.isPending ? <ButtonSpinner /> : null}
          {t("kyc.cases.create")}
        </Button>
      </div>
    );
  }

  if (!kycCase) return null;

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm">
        <NoTranslate as="span">{kycCase.id}</NoTranslate> —{" "}
        <NoTranslate as="span">
          <Badge variant="outline">{kycCase.status}</Badge>
        </NoTranslate>
      </p>
      <KycCaseDocuments
        kycCaseId={kycCase.id}
        documents={kycCase.documents ?? []}
        caseStatus={kycCase.status}
      />
      {kycCase.status === "DRAFT" ? (
        <Button
          className="self-start"
          disabled={submitCase.isPending || (kycCase.documents ?? []).length === 0}
          onClick={() =>
            submitCase.mutate(undefined, {
              onSuccess: () => notifSilence(t("kyc.cases.feedback.submitted")),
              onError: (error) => notifSilenceError(apiErrorHandler(error)),
            })
          }
        >
          {submitCase.isPending ? <ButtonSpinner /> : null}
          {t("kyc.cases.submit")}
        </Button>
      ) : null}
    </div>
  );
}

function StepScreening({
  partyId,
  done,
  requested,
  onRequested,
}: {
  partyId: string;
  done: boolean;
  requested: boolean;
  onRequested: () => void;
}) {
  const { t } = useTypedTranslation();
  const { t: tKyc } = useKycTranslation();
  const [reference, setReference] = useState("");
  const [reason, setReason] = useState("");
  const submitApproval = useSubmitApprovalRequest();
  const { refetch: refetchParty, isFetching: rechecking } = useParty(partyId);

  if (done) {
    return <p className="text-sm text-success">{t("kyc.onboarding.screening.clear")}</p>;
  }

  const handleRecheck = async () => {
    const { data: fresh } = await refetchParty();
    if (fresh?.sanctionsScreeningStatus === "CLEAR") {
      return;
    }
    notifSilenceWarning(tKyc("kyc.onboarding.screening.stillPending"));
  };

  return (
    <RequireRole
      roles={[FLASHPAY_ROLES.SUPERADMIN, FLASHPAY_ROLES.ADMIN]}
      fallback={<RoleRequiredHint />}
    >
      {requested ? (
        <div className="flex max-w-lg flex-col gap-3">
          <p className="text-sm text-muted-foreground">
            {tKyc("kyc.onboarding.screening.pendingApproval")}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              className="self-start"
              disabled={rechecking}
              onClick={() => void handleRecheck()}
            >
              {rechecking ? <ButtonSpinner /> : null}
              {tKyc("kyc.onboarding.screening.recheck")}
            </Button>
            <Button variant="link" className="h-auto px-0" asChild>
              <Link to="/app/admin/approvals">
                {tKyc("kyc.onboarding.screening.approvalsLink")}
              </Link>
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex max-w-md flex-col gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="screening-ref">{t("kyc.onboarding.screening.reference")}</Label>
            <Input
              id="screening-ref"
              className="notranslate"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder={tKyc("kyc.onboarding.screening.referencePlaceholder")}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="screening-reason">
              {tKyc("kyc.onboarding.screening.reason")}
            </Label>
            <Input
              id="screening-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={tKyc("kyc.onboarding.screening.reasonPlaceholder")}
            />
          </div>
          <Button
            className="self-start"
            disabled={submitApproval.isPending || !reason.trim()}
            onClick={() =>
              submitApproval.mutate(
                {
                  actionType: "KYC_FORCE_OVERRIDE",
                  sourceService: "kyc-service",
                  resourceType: "Party",
                  resourceId: partyId,
                  payload: {
                    status: "CLEAR",
                    ...(reference.trim() ? { reference: reference.trim() } : {}),
                  },
                  reason: reason.trim(),
                },
                {
                  onSuccess: () => {
                    notifSilence(tKyc("kyc.onboarding.screening.requested"));
                    onRequested();
                  },
                  onError: (error) => notifSilenceError(apiErrorHandler(error)),
                }
              )
            }
          >
            {submitApproval.isPending ? <ButtonSpinner /> : null}
            {tKyc("kyc.onboarding.screening.submitForApproval")}
          </Button>
        </div>
      )}
    </RequireRole>
  );
}

function StepReview({ kycCaseId }: { kycCaseId: string }) {
  const { t } = useTypedTranslation();
  const [decision, setDecision] = useState<"APPROVED" | "REJECTED" | null>(null);
  return (
    <RequireRole
      roles={[FLASHPAY_ROLES.SUPERADMIN, FLASHPAY_ROLES.ADMIN]}
      fallback={<RoleRequiredHint />}
    >
      <div className="flex gap-2">
        <Button onClick={() => setDecision("APPROVED")}>{t("kyc.cases.approve")}</Button>
        <Button variant="destructive" onClick={() => setDecision("REJECTED")}>
          {t("kyc.cases.reject")}
        </Button>
        {decision ? (
          <KycCaseReviewDialog
            open={decision !== null}
            onOpenChange={(open) => {
              if (!open) setDecision(null);
            }}
            kycCaseId={kycCaseId}
            decision={decision}
          />
        ) : null}
      </div>
    </RequireRole>
  );
}

function StepActivation({
  walletId,
  kycLevel,
}: {
  walletId: string;
  kycLevel: WalletKycLevel;
}) {
  const { t } = useTypedTranslation();
  const approve = useApproveWalletKyc(walletId);
  return (
    <RequireRole
      roles={[
        FLASHPAY_ROLES.SERVICE,
        FLASHPAY_ROLES.SUPERADMIN,
        FLASHPAY_ROLES.ADMIN,
      ]}
      fallback={<RoleRequiredHint />}
    >
      <div className="flex flex-col gap-2">
        <p className="text-sm text-muted-foreground">
          {t("kyc.onboarding.activation.hint")}
        </p>
        <Button
          className="self-start"
          disabled={approve.isPending}
          onClick={() =>
            approve.mutate(
              { kycLevel },
              {
                onSuccess: () => {
                  notifSilence(t("kyc.onboarding.activation.done"));
                },
                onError: (error) => notifSilenceError(apiErrorHandler(error)),
              }
            )
          }
        >
          {approve.isPending ? <ButtonSpinner /> : null}
          {t("kyc.onboarding.activation.activate")}
        </Button>
      </div>
    </RequireRole>
  );
}
